import { Children, Fragment, createElement, isValidElement } from 'react'
import type {
  CSSProperties,
  ElementType,
  ReactElement,
  ReactNode,
} from 'react'
import { clsx } from 'clsx'
import type { SpaceType, SpacingProps } from '../../shared/types'
import Space from '../space/Space'
import {
  createSpacing,
  isValidSpaceProp,
  removeSpaceProps,
} from '../space/SpacingUtils'
import type { FlexEnd, FlexStart } from './types'
import type { ComponentMarkers } from '../../shared/helpers/withComponentMarkers'

export const omitSpacingProps = removeSpaceProps

type SpacingElementProps = Partial<
  Record<FlexStart | FlexEnd, SpaceType>
> & {
  space?: Partial<Record<FlexStart | FlexEnd, SpaceType>>
}

/**
 * Picks the spacing props from the given props object.
 * @template Props - The type of the props object.
 * @param {Props} props - The props object.
 * @returns {SpacingProps} - The spacing props object.
 */
export function pickSpacingProps<Props extends SpacingProps>(
  props: Props
): SpacingProps {
  const obj: SpacingProps = {}
  for (const key in props as SpacingProps) {
    if (isValidSpaceProp(key) && typeof props[key] !== 'undefined') {
      obj[key] = props[key]
    }
  }
  return obj
}

/**
 * Retrieves the space value of a Flex component based on the specified type and element.
 * @param type - The type of space value to retrieve (Start or End).
 * @param element - The React element to extract the space value from.
 * @returns The space value of the element, or undefined if it cannot be determined.
 */
export function getSpaceValue(
  type: FlexStart | FlexEnd,
  element: ReactNode
): SpaceType | undefined {
  if (!isValidElement<SpacingElementProps>(element)) {
    return undefined
  }

  const elementProps = element.props || {}

  return (
    elementProps?.[type] ??
    (typeof elementProps?.space === 'object'
      ? elementProps.space[type]
      : undefined)
  )
}

/**
 * Checks if the provided element is a heading element.
 * @param element - The element to check.
 * @returns `true` if the element is a heading element, `false` otherwise.
 */
export function isHeadingElement(element: ReactNode): boolean {
  return (
    isValidElement(element) &&
    (element?.type as ComponentMarkers)?._isHeadingElement === true
  )
}

/**
 * Determines the spacing variant of a React node element.
 * @param element - The React node element to check.
 * @returns The spacing variant (true, false or "children") of the element, or undefined if it does not support spacing props.
 */
export function getSpaceVariant(element: ReactNode) {
  if (isValidElement<SpacingElementProps>(element)) {
    if (element?.type === Fragment) {
      return 'children'
    }

    const check = (element?.type as ComponentMarkers)
      ?._supportsSpacingProps
    if (typeof check !== 'undefined') {
      return check
    }

    const keys = ['space', 'top', 'right', 'bottom', 'left']
    const props = element?.props ?? {}
    if (keys.some((key) => key in (props as object))) {
      return true
    }
  }

  return undefined
}

/**
 * Renders an element with spacing props applied.
 * If the element is a component that accepts spacing props, the props are directly applied.
 * If the element is a component that has children and accepts spacing props, the props are applied to the children.
 * If the element does not accept spacing props, the element is returned as is.
 *
 * @param element - The element to render with spacing props.
 * @param spaceProps - The spacing props to apply.
 * @returns The rendered element with spacing props applied.
 */
export function renderWithSpacing(
  element: ReactNode,
  spaceProps: SpacingProps & {
    key?: string
    className?: string
    wrapInSpace?: boolean
  }
) {
  const variant = getSpaceVariant(element)
  const { wrapInSpace = true } = spaceProps

  if (variant === false) {
    return element
  }

  if (variant === 'passthrough') {
    const childElement = element as ReactElement<{ children?: ReactNode }>
    const children = childElement?.props?.children
    const childKey = childElement?.key
    const childProps = childElement?.props || {}

    return Children.toArray(children).map((child, i) => {
      const wrapped = wrapWithSpace({
        element: child as ReactNode,
        spaceProps,
        wrapInSpace,
      })

      return createElement(
        childElement.type as ElementType,
        { ...childProps, key: childKey ? `${childKey}-${i}` : `${i}` },
        wrapped
      )
    })
  }

  if (variant === 'children') {
    return (Children.toArray(element) as ReactElement[]).map(
      (child: ReactElement<{ children?: ReactNode }>) => {
        const children = child?.props?.children
        const childKey = child?.key
        const childProps = child?.props || {}

        return Children.toArray(children).map((element, i) => {
          return createElement(
            child.type as ElementType,
            { ...childProps, key: childKey ? `${childKey}-${i}` : i },
            wrapWithSpace({
              element: element as ReactNode,
              spaceProps,
              wrapInSpace,
            })
          )
        })
      }
    )
  }

  return wrapWithSpace({ element, spaceProps, variant, wrapInSpace })
}

function isIntrinsicElement(element: ReactNode) {
  return isValidElement(element) && typeof element.type === 'string'
}

function wrapWithSpace({
  element,
  spaceProps,
  variant = null,
  wrapInSpace = true,
}) {
  const resolvedVariant = variant ?? getSpaceVariant(element)
  const { wrapInSpace: _, key, ...props } = spaceProps

  if (resolvedVariant === true) {
    return createElement((element as ReactElement).type as ElementType, {
      ...((element as ReactElement).props as Record<string, unknown>),
      key,
      ...props,
    })
  }

  if (resolvedVariant === 'children') {
    return renderWithSpacing(element, spaceProps)
  }

  if (!wrapInSpace && isIntrinsicElement(element)) {
    return cloneIntrinsicElementWithSpacing(element, spaceProps)
  }

  return (
    <Space key={key} {...props}>
      {element}
    </Space>
  )
}

function cloneIntrinsicElementWithSpacing(
  element: ReactNode,
  {
    wrapInSpace,
    className,
    style,
    ...spaceProps
  }: SpacingProps & {
    key?: string
    className?: string
    style?: CSSProperties
    wrapInSpace?: boolean
  }
) {
  if (
    !isValidElement<{ className?: string; style?: CSSProperties }>(element)
  ) {
    return element
  }

  const elementProps = element.props || {}

  const spacing = createSpacing(spaceProps)

  return createElement((element as ReactElement).type as ElementType, {
    ...((element as ReactElement).props as Record<string, unknown>),
    key: spaceProps.key,
    className: clsx(
      elementProps?.className,
      ...spacing.className,
      className
    ),
    style: {
      ...elementProps?.style,
      ...spacing.style,
      ...style,
    },
  })
}
